import React, { useCallback, useEffect, useState } from "react";
import { Users, X, Check } from "lucide-react";
import ConversationList from "./ConversationList";
import ConversationHeader from "./ConversationHeader";
import MessageThread from "./MessageThread";
import MessageComposer from "./MessageComposer";
import EmptyConversationState from "./EmptyConversationState";
import ConversationSettingsDrawer from "./ConversationSettingsDrawer";
import useConversationSettings from "../../hooks/messages/useConversationSettings";
import GameInviteModal from "../Games/GameInviteModal";
import GameBoard from "../Games/GameBoard";

const GAME_TYPE_LABELS = {
  connect4: "Connect 4",
  checkers: "Checkers",
  chess: "Chess",
  battleship: "Battleship",
};

function formatGameTypeLabel(gameType) {
  if (!gameType) return "a game";
  return GAME_TYPE_LABELS[gameType] || String(gameType);
}

export default function MessagesLayout({
  friends,
  dmContactsById,
  groups,
  inboxItems,
  searchQuery,
  setSearchQuery,
  selectedFriend,
  setSelectedFriend,
  selectedGroup,
  setSelectedGroup,
  selectedFriendId,
  selectedGroupId,
  isGroupChat,
  onlineFriends,
  typingUsers,
  groupTypingUsers,
  currentUserId,
  activeMessages,
  inputMessage,
  handleInputChange,
  handleSendMessage,
  sendGifMessage,
  insertEmojiAtRange,
  isEmojiOpen,
  setIsEmojiOpen,
  isGifOpen,
  setIsGifOpen,
  gifQuery,
  setGifQuery,
  gifItems,
  gifLoading,
  gifError,
  messageListRef,
  computeStickToBottom,
  stickToBottomRef,
  formatMessageTime,
  isProbablyGifUrl,
  isCreatingGroup,
  setIsCreatingGroup,
  groupName,
  setGroupName,
  selectedGroupMembers,
  toggleMemberSelection,
  handleCreateGroup,
  handleLeaveGroup,
  handleDisbandGroup,
  uploadChatFile,
  fileUploadBusy,
  games,
}) {
  const apiBase = process.env.REACT_APP_API_BASE || "";
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    if (isGroupChat || !selectedFriend) {
      games?.setIsGameInviteOpen?.(false);
    }
  }, [isGroupChat, selectedFriend, games]);

  const conv = useConversationSettings({
    apiBase,
    isGroupChat,
    selectedFriendId,
    selectedGroupId,
    peerUsername: selectedFriend?.username,
  });

  const headerDisplayTitle = isGroupChat
    ? selectedGroup?.name
    : conv.displayTitleDm || selectedFriend?.username;

  const resolveSenderLabel = useCallback(
    (senderId, username) => {
      if (!isGroupChat) return username;
      return conv.resolveGroupMemberLabel(senderId, username);
    },
    [isGroupChat, conv]
  );

  const handleThemeSelect = useCallback(
    async (key) => {
      try {
        await conv.updateTheme(key);
      } catch (e) {
        console.error(e);
      }
    },
    [conv]
  );

  const handlePickChatImage = useCallback(
    async (file) => {
      const ok = await uploadChatFile?.(file);
      if (ok) await conv.refreshMedia?.();
    },
    [uploadChatFile, conv]
  );

  const onSelectConversation = (item) => {
    if (item.type === "dm") {
      const friend =
        friends.find((f) => Number(f.user_id) === Number(item.keyId)) ||
        dmContactsById?.[Number(item.keyId)] ||
        null;
      if (friend) {
        setSelectedFriend(friend);
        setSelectedGroup(null);
      }
      return;
    }
    const group = groups.find((g) => Number(g.group_id) === Number(item.keyId));
    if (group) {
      setSelectedGroup(group);
      setSelectedFriend(null);
    }
  };

  const showTyping = isGroupChat
    ? groupTypingUsers[selectedGroupId]?.size > 0
    : typingUsers.has(selectedFriendId);

  return (
    <div className="relative flex min-h-0 w-full min-w-0 flex-1 flex-col lg:grid lg:h-full lg:grid-cols-[minmax(260px,370px)_minmax(0,1fr)]">
      <ConversationList
        inboxItems={inboxItems}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSelectConversation={onSelectConversation}
        onNewGroup={() => setIsCreatingGroup(true)}
        onlineFriends={onlineFriends}
      />

      <section
        className={`flex min-h-0 min-w-0 flex-1 flex-col transition-colors duration-300 lg:h-full ${
          selectedFriend || selectedGroup ? conv.threadClass : "bg-[#0d1320]"
        }`}
      >
        {!selectedFriend && !selectedGroup ? (
          <EmptyConversationState />
        ) : (
          <>
            <ConversationHeader
              isGroupChat={isGroupChat}
              selectedGroup={selectedGroup}
              selectedFriend={selectedFriend}
              currentUserId={currentUserId}
              onlineFriends={onlineFriends}
              selectedFriendId={selectedFriendId}
              typingUsers={typingUsers}
              groupTypingUsers={groupTypingUsers}
              selectedGroupId={selectedGroupId}
              onLeaveGroup={handleLeaveGroup}
              onDisbandGroup={handleDisbandGroup}
              displayTitle={headerDisplayTitle}
              onOpenConversationSettings={() => setSettingsOpen(true)}
              onPlayGame={
                !isGroupChat && selectedFriend && games
                  ? () => games.setIsGameInviteOpen(true)
                  : undefined
              }
              playGameDisabled={!games?.gameChannelReady}
            />

            {isGroupChat && selectedGroup?.members && (
              <div className="px-7 py-3 border-b border-white/10 bg-white/[0.01]">
                <div className="flex flex-wrap gap-2">
                  {selectedGroup.members.map((member) => {
                    const memberLabel = conv.resolveGroupMemberLabel(member.user_id, member.username);
                    return (
                    <div
                      key={member.user_id}
                      className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5"
                    >
                      {member.profile_picture ? (
                        <img
                          src={member.profile_picture}
                          alt={memberLabel}
                          className="w-5 h-5 rounded-full border border-white/10"
                        />
                      ) : (
                        <div className="w-5 h-5 rounded-full bg-white/10 border border-white/10 grid place-items-center text-[10px] text-white/70">
                          {memberLabel?.[0]?.toUpperCase()}
                        </div>
                      )}
                      <span className="text-[12px] font-medium text-white/85">
                        {memberLabel}
                        {member.user_id === currentUserId && (
                          <span className="ml-1 text-white/45">(you)</span>
                        )}
                      </span>
                    </div>
                  );
                  })}
                </div>
              </div>
            )}

            <MessageThread
              messages={activeMessages}
              messageListRef={messageListRef}
              onScroll={() => {
                stickToBottomRef.current = computeStickToBottom();
              }}
              isGroupChat={isGroupChat}
              currentUserId={currentUserId}
              formatMessageTime={formatMessageTime}
              isProbablyGifUrl={isProbablyGifUrl}
              showTyping={showTyping}
              resolveSenderLabel={resolveSenderLabel}
              bubbleToneClass={conv.bubbleClasses}
            />

            <ConversationSettingsDrawer
              open={settingsOpen}
              onClose={() => setSettingsOpen(false)}
              isGroupChat={isGroupChat}
              selectedGroup={selectedGroup}
              selectedFriend={selectedFriend}
              settingsLoading={conv.loading}
              themeKey={conv.themeKey}
              onSelectTheme={handleThemeSelect}
              myNicknameForPeer={conv.settings?.myNicknameForPeer}
              nicknamesByUserId={conv.settings?.nicknamesByUserId || {}}
              currentUserId={currentUserId}
              mediaItems={conv.mediaItems}
              isProbablyGifUrl={isProbablyGifUrl}
              onRefreshMedia={conv.refreshMedia}
              onSaveDmNickname={conv.saveDmNickname}
              onSaveGroupNickname={conv.saveGroupNickname}
            />

            <MessageComposer
              value={inputMessage}
              onChange={handleInputChange}
              onSubmit={handleSendMessage}
              onSendGif={sendGifMessage}
              onInsertEmojiAtRange={insertEmojiAtRange}
              onPickImageFile={handlePickChatImage}
              fileUploading={!!fileUploadBusy}
              isEmojiOpen={isEmojiOpen}
              setIsEmojiOpen={setIsEmojiOpen}
              isGifOpen={isGifOpen}
              setIsGifOpen={setIsGifOpen}
              gifQuery={gifQuery}
              setGifQuery={setGifQuery}
              gifItems={gifItems}
              gifLoading={gifLoading}
              gifError={gifError}
            />
          </>
        )}
      </section>

      {games && !isGroupChat && selectedFriend && (
        <GameInviteModal
          isOpen={games.isGameInviteOpen}
          onClose={() => games.setIsGameInviteOpen(false)}
          friend={selectedFriend}
          onInvite={(gameType) => games.sendGameInvite(selectedFriend.user_id, gameType)}
        />
      )}

      {games?.pendingInvite && (
        <div
          className="fixed bottom-5 left-1/2 z-[85] w-[min(92vw,420px)] -translate-x-1/2 rounded-2xl border border-white/12 bg-[#0a0e16]/95 px-4 py-3 shadow-[0_16px_50px_rgba(0,0,0,0.55)] backdrop-blur-xl"
          role="dialog"
          aria-modal="true"
          aria-labelledby="game-invite-title"
        >
          <div id="game-invite-title" className="text-[13px] font-semibold text-white">
            Game invite
          </div>
          <p className="mt-1 text-[12px] text-white/60 leading-snug">
            <span className="text-white/85">{games.pendingInvite.opponentName}</span> wants to play{" "}
            <span className="text-emerald-200/90">{formatGameTypeLabel(games.pendingInvite.gameType)}</span>
            with you.
          </p>
          <div className="mt-3 flex gap-2 justify-end">
            <button
              type="button"
              onClick={games.declineGameInvite}
              className="h-9 rounded-xl border border-white/12 bg-white/[0.04] px-3 text-[12px] font-medium text-white/80 hover:bg-white/[0.08] transition focus:outline-none focus:ring-2 focus:ring-white/20"
            >
              Decline
            </button>
            <button
              type="button"
              onClick={games.acceptGameInvite}
              className="h-9 rounded-xl border border-emerald-400/20 bg-emerald-500/15 px-3 text-[12px] font-semibold text-emerald-100 hover:bg-emerald-500/25 transition focus:outline-none focus:ring-2 focus:ring-emerald-400/30"
            >
              Accept
            </button>
          </div>
        </div>
      )}

      {games?.activeGame?.gameState && (
        <GameBoard
          gameState={games.activeGame.gameState}
          gameType={games.activeGame.gameType}
          currentUserId={currentUserId}
          opponentName={games.activeGame.opponentName}
          opponentId={games.activeGame.opponentId}
          onMove={games.submitGameMove}
          onForfeit={games.forfeitActiveGame}
          onClose={games.dismissActiveGame}
        />
      )}

      {isCreatingGroup && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm"
            onClick={() => setIsCreatingGroup(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="w-full max-w-md rounded-3xl border border-white/10 bg-slate-950/92 backdrop-blur-2xl shadow-[0_20px_70px_rgba(0,0,0,0.65)] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-4 border-b border-white/10 bg-white/[0.02]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="h-9 w-9 rounded-2xl bg-white/[0.06] border border-white/14 flex items-center justify-center">
                      <Users className="text-white/88" size={18} />
                    </div>
                    <div>
                      <h2 className="text-white font-semibold">New Group Chat</h2>
                      <p className="text-[12px] text-white/55">Choose friends and start messaging</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsCreatingGroup(false)}
                    className="h-8 w-8 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition flex items-center justify-center"
                  >
                    <X className="text-white/85" size={16} />
                  </button>
                </div>
              </div>

              <div className="px-5 py-4 max-h-[60vh] overflow-y-auto fc-scroll">
                <div className="mb-4">
                  <label className="block text-[13px] font-medium text-white/85 mb-2">Group Name</label>
                  <input
                    type="text"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder="Enter group name (3-100 characters)"
                    maxLength={100}
                    className="w-full rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-white placeholder:text-white/35 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-white/20 transition"
                  />
                </div>

                <div>
                  <label className="block text-[13px] font-medium text-white/85 mb-2">
                    Select Friends ({selectedGroupMembers.length} selected)
                  </label>
                  <div className="space-y-2">
                    {friends.length === 0 ? (
                      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-center text-[13px] text-white/55">
                        No friends available
                      </div>
                    ) : (
                      friends.map((friend) => {
                        const isSelected = selectedGroupMembers.includes(friend.user_id);
                        return (
                          <button
                            key={friend.user_id}
                            type="button"
                            onClick={() => toggleMemberSelection(friend.user_id)}
                            className={`w-full flex items-center gap-3 rounded-2xl border px-3 py-2.5 transition ${
                              isSelected
                                ? "bg-white/[0.08] border-white/18"
                                : "bg-white/[0.02] border-white/10 hover:bg-white/[0.05]"
                            }`}
                          >
                            {friend.profile_picture ? (
                              <img
                                src={friend.profile_picture}
                                alt={friend.username}
                                className="w-10 h-10 rounded-2xl object-cover border border-white/10"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center text-white font-semibold">
                                {friend.username?.[0]?.toUpperCase()}
                              </div>
                            )}
                            <div className="flex-1 min-w-0 text-left">
                              <div className="text-[13px] font-medium text-white truncate">{friend.username}</div>
                            </div>
                            <div
                              className={`h-5 w-5 rounded-lg border-2 flex items-center justify-center transition ${
                                isSelected ? "bg-white/[0.12] border-white/70" : "bg-white/[0.03] border-white/20"
                              }`}
                            >
                              {isSelected && <Check className="text-white" size={14} />}
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              <div className="px-5 py-4 border-t border-white/10 bg-white/[0.02] flex gap-3">
                <button
                  onClick={() => setIsCreatingGroup(false)}
                  className="flex-1 h-11 rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition text-white/85 font-medium text-[13.5px]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateGroup}
                  disabled={!groupName.trim() || groupName.trim().length < 3 || selectedGroupMembers.length === 0}
                  className="flex-1 h-11 rounded-2xl border border-white/16 bg-white/[0.08] hover:bg-white/[0.12] transition text-white font-semibold text-[13.5px] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Create Group
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
